import { PageContent } from 'Components'
import { ReportHubProvider } from '../Contexts/ReportHubContext'
import {
  ReportHubDashboardHeader,
  ReportHubMetricCard,
  ReportHubCampaignPerformanceChart,
  ReportHubTopPerformingTable,
  ReportHubErrorAlert,
} from '../Components/ReportHub'

import { useReportHub } from '../Contexts/useReportHub'
import { DashboardMetric } from '../Components/ReportHub/types'
import { useMemo } from 'react'
import { ReportHubTable } from '../Components/ReportHub/ReportHubTable'
import { ReportExportModal } from '../Components/ReportHub/ReportExportModal'
import { Fa } from '@eltoro-ui/components'

// Helper function to format numbers with comma separators
const formatNumber = (num: number): string => {
  return num.toLocaleString()
}

// Helper function to format currency with comma separators and two decimal places
const formatCurrency = (num: number): string => {
  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

// Skeleton loading component for metric cards
const MetricCardSkeleton = () => {
  return (
    <div className="border-tint-gray-300 bg-base animate-pulse rounded-lg border p-4 shadow-sm">
      {/* Icon and title skeleton */}
      <div className="mb-3 flex items-center gap-3">
        <div className="h-5 w-5 rounded bg-gray-200"></div>
        <div className="h-4 w-24 rounded bg-gray-200"></div>
      </div>

      {/* Current value skeleton */}
      <div className="mb-2 h-[4.25rem] w-20 rounded bg-gray-200"></div>

      {/* Total value skeleton (optional, sometimes present) */}
      <div className="h-3 w-16 rounded bg-gray-100"></div>
    </div>
  )
}

// Skeleton loading component for campaign performance chart and top performing
const LargeCardSkeleton = () => {
  return (
    <div
      className="ReportHubCampaignPerformanceChart bg-base border-tint-gray-300 h-[30rem] animate-pulse rounded-lg border p-6"
      aria-labelledby="chart-title"
    />
  )
}

const ReportHubMetrics = () => {
  const {
    overviewStats,
    isLoading,
    view,
    orderLines,
    campaigns,
    hasErrors,
    allErrors,
  } = useReportHub()

  // Check if there's no data available
  const hasNoData =
    !isLoading &&
    (!orderLines || orderLines.length === 0) &&
    (!campaigns || campaigns.length === 0)

  // Check if we have data but stats failed (partial error state)
  const hasDataButStatsError =
    !isLoading &&
    ((orderLines && orderLines.length > 0) ||
      (campaigns && campaigns.length > 0)) &&
    hasErrors &&
    allErrors.some((error) => error.includes('stats'))

  const metrics: DashboardMetric[] = useMemo(() => {
    // Create empty state metrics when there's no data
    if (hasNoData || !overviewStats) {
      return [
        {
          title: 'Impressions Served',
          current: hasDataButStatsError ? 'Error' : '-',
          format: 'number',
          icon: 'chart-line',
          subTitle:
            view === 'campaign'
              ? hasDataButStatsError
                ? 'Stats unavailable'
                : 'No Active Campaigns'
              : hasDataButStatsError
              ? 'Stats unavailable'
              : 'No Active Order Lines',
        },
        {
          title: 'Budget Utilization',
          current: hasDataButStatsError ? 'Error' : '-',
          format: 'currency',
          icon: 'dollar-sign',
          subTitle: hasDataButStatsError ? 'Stats unavailable' : undefined,
        },
        {
          title: 'CTR',
          current: hasDataButStatsError ? 'Error' : '-',
          format: 'percentage',
          icon: 'chart-bar',
          subTitle: hasDataButStatsError ? 'Stats unavailable' : 'No Clicks',
        },
      ]
    }

    const metricsArray: DashboardMetric[] = [
      {
        title: 'Impressions Served',
        current: formatNumber(overviewStats.totalImpressions || 0),
        format: 'number',
        icon: 'chart-line',
        subTitle:
          view === 'campaign'
            ? `${formatNumber(overviewStats.activeCampaigns)} Active Campaigns`
            : `${formatNumber(
                overviewStats.activeOrderLines,
              )} Active Order Lines`,
      },
      {
        title: 'Budget Utilization',
        current: formatCurrency(overviewStats.budget?.current || 0),
        total: overviewStats.budget?.total
          ? formatCurrency(overviewStats.budget.total)
          : undefined,
        format: 'currency',
        icon: 'dollar-sign',
      },
      {
        title: 'CTR',
        current: `${(overviewStats.ctr?.current || 0).toFixed(2)}%`,
        format: 'percentage',
        icon: 'chart-bar',
        subTitle: `${formatNumber(overviewStats.totalClicks)} Total Clicks`,
      },
    ]

    return metricsArray
  }, [overviewStats, view, hasNoData, hasDataButStatsError])

  // Show skeleton loading when data is loading
  if (isLoading) {
    return (
      <div className="mb-6 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Show 3 skeleton cards as default */}
        {Array.from({ length: 3 }).map((_, index) => (
          <MetricCardSkeleton key={`skeleton-${index}`} />
        ))}
      </div>
    )
  }

  return (
    <div className="mb-6 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
      {metrics.map((metric, index) => (
        <ReportHubMetricCard key={index} metric={metric} />
      ))}
    </div>
  )
}

// Chart and Table section with loading states
const ReportHubChartsAndTables = () => {
  const {
    isLoading,
    topPerformers,
    view,
    filteredOlData,
    filteredCampaignData,
    filters,
    updateView,
    orderLines,
    campaigns,
    displayModal,
    updateDisplayModal,
    hasErrors,
    allErrors,
    statsProgress,
  } = useReportHub()
  // Check if there's no data available
  const hasNoData =
    !isLoading &&
    (!orderLines || orderLines.length === 0) &&
    (!campaigns || campaigns.length === 0)

  // Check if we have data but stats failed
  const hasDataButStatsError =
    !isLoading &&
    ((orderLines && orderLines.length > 0) ||
      (campaigns && campaigns.length > 0)) &&
    hasErrors &&
    allErrors.some((error) => error.includes('stats'))

  if (hasNoData) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:gap-6 xl:grid-cols-2">
        {/* No data message spanning both columns */}
        <div className="bg-base border-tint-gray-300 flex flex-col items-center justify-center rounded-lg border p-12 text-center xl:col-span-2">
          <Fa icon="chart-simple" size={5} className="text-primary mb-2" />
          <h3 className="text-tint-gray-900 mb-2 text-xl font-semibold">
            No Campaign Data Available
          </h3>
          <p className="text-tint-gray-600 mb-4 max-w-md">
            No campaigns or order lines are currently available. Try adjusting
            your filters or including child organizations to view historical
            performance metrics.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:gap-6 xl:grid-cols-2">
      {statsProgress.total ? (
        <section className="StatsLoadingSection col-span-2">
          <div className="bg-base border-tint-gray-300 rounded-lg border p-4">
            <p className="text-tint-gray-900 mb-3 font-medium">
              Fetching Stats Data {statsProgress.current} /{' '}
              {statsProgress.total} (
              {statsProgress.total > 0
                ? Math.round(
                    (statsProgress.current / statsProgress.total) * 100,
                  )
                : 0}
              %)
            </p>
            {/* Custom Progress Bar */}
            <div
              className="w-full rounded-full border"
              style={{
                height: '12px',
                backgroundColor: '#e5e7eb',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${
                    statsProgress.total > 0
                      ? Math.round(
                          (statsProgress.current / statsProgress.total) * 100,
                        )
                      : 0
                  }%`,
                  height: '100%',
                  backgroundColor: '#3b82f6',
                  borderRadius: '9999px',
                  transition: 'width 0.5s ease-out',
                }}
              />
            </div>
          </div>
        </section>
      ) : null}
      {/* Campaign Performance Chart */}
      {isLoading ? (
        <LargeCardSkeleton />
      ) : (
        <ReportHubCampaignPerformanceChart />
      )}

      {/* Top Performing Order Lines Table */}
      {isLoading ? (
        <LargeCardSkeleton />
      ) : (
        <ReportHubTopPerformingTable
          data={hasDataButStatsError ? [] : topPerformers}
          view={view}
        />
      )}

      <div className="col-span-2">
        {isLoading ? (
          <LargeCardSkeleton />
        ) : (
          <ReportHubTable
            orderLineData={filteredOlData}
            campaignData={filteredCampaignData}
            view={view}
            includeChildOrgs={filters.includeChildOrganizations}
            updateView={updateView}
          />
        )}
      </div>
      <div className="ExportModal">
        <ReportExportModal
          isOpen={displayModal}
          tableData={
            view === 'orderLine' ? filteredOlData : filteredCampaignData
          }
          view={view}
          includeChild={filters.includeChildOrganizations}
          onClose={() => updateDisplayModal(false)}
        />
      </div>
    </div>
  )
}

export const ReportHubOverviewPage = () => {
  return (
    <ReportHubProvider>
      <ReportHubOverviewContent />
    </ReportHubProvider>
  )
}

// Main content component that has access to the ReportHub context
const ReportHubOverviewContent = () => {
  return (
    <PageContent className="ReportHubOverviewPage">
      <ReportHubDashboardHeader />
      {/* Error Alert - Display any errors that occurred during data fetching */}
      <ReportHubErrorAlert />
      {/* Metrics Row */}
      <ReportHubMetrics />
      {/* Chart and Table Row */}
      <ReportHubChartsAndTables />
    </PageContent>
  )
}
